import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { getAllCoursesQuery } from '@pm2/course-builder';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { useActivityIndicator, CSSRefTransition } from '@utils/react';
import { ErrorMessage } from '../../atoms/Error/Error.js';
import { Loader } from '../../atoms/loader.js';
import { CourseTable } from './components/course-table.js';
import { Topbar } from './components/topbar.js';
import { useCoursesWithNotifications } from './hook.courses-with-notifications.js';
import { useFilteredCourses } from './hooks/filtered-courses.js';
import { content, ENTER_TRANSITION_DURATION_MS, transitionCls } from './page.styles.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { getNotificationsQuery } from '../notifications/api.get-notifications.query.js';

function CourseBuilderPage() {
  const courses = useCoursesWithNotifications();
  const activityIndicator = useActivityIndicator(courses.isLoading, {
    allowInitial: true
  });
  const [filteredCourses, filterOptions, filterCallbacks] = useFilteredCourses(courses.data ?? []);
  return /*#__PURE__*/jsx(TransitionGroup, {
    className: content,
    children: activityIndicator.isActive() ? /*#__PURE__*/jsx(CSSRefTransition, {
      classNames: transitionCls,
      timeout: ENTER_TRANSITION_DURATION_MS,
      children: ref => /*#__PURE__*/jsx(Loader, {
        ref: ref
      })
    }, "loader") : /*#__PURE__*/jsx(CSSRefTransition, {
      classNames: transitionCls,
      timeout: ENTER_TRANSITION_DURATION_MS,
      children: ref => /*#__PURE__*/jsxs("section", {
        ref: ref,
        children: [/*#__PURE__*/jsx(Topbar, {
          courses: courses.data ?? [],
          filterOptions: filterOptions,
          filterCallbacks: filterCallbacks
        }), courses.isError || courses.isLoading ? /*#__PURE__*/jsx(CourseErrorMessage, {}) : /*#__PURE__*/jsx(CourseTable, {
          courses: filteredCourses,
          filterOptions: filterOptions
        })]
      })
    }, "content")
  });
}
function CourseErrorMessage() {
  const notifier = useNotifier();
  const client = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const [isLoading, setLoading] = useState(false);
  const handleRetry = async () => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    try {
      await Promise.all([client.fetchQuery(getAllCoursesQuery.queryConfig({
        user
      })), client.fetchQuery(getNotificationsQuery(user))]);
    } catch {
      notifier.notify({
        id: "course-builder.page.error",
        type: "error",
        body: "Noget gik galt under indlæsningen, prøv venligst igen om lidt."
      });
    } finally {
      setLoading(false);
    }
  };
  return /*#__PURE__*/jsx(ErrorMessage, {
    onRetry: handleRetry,
    isLoading: isLoading
  });
}

export { CourseBuilderPage };
//# sourceMappingURL=page.js.map
